import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { FaTimes, FaTrash, FaMinus, FaPlus, FaWhatsapp, FaShoppingCart } from 'react-icons/fa'
import { useCart } from '../context/CartContext'

export default function CartPanel() {
  const { t } = useTranslation()
  const {
    items,
    totalUnits,
    subtotal,
    formatPrice,
    increment,
    decrement,
    removeItem,
    clearCart,
    isCartOpen,
    setIsCartOpen,
    whatsappUrl,
  } = useCart()

  const panelRef = useRef(null)

  useEffect(() => {
    if (!isCartOpen) return
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsCartOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isCartOpen, setIsCartOpen])

  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isCartOpen])

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm"
            onClick={() => setIsCartOpen(false)}
          />

          {/* Panel */}
          <motion.aside
            ref={panelRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            className="fixed top-0 right-0 z-[10001] h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-ink">{t('cart.title', 'Shopping Cart')}</h2>
                <p className="text-xs text-ash">
                  {totalUnits > 0
                    ? t('cart.items_count', { count: totalUnits, defaultValue: `${totalUnits} item(s)` })
                    : t('cart.empty', 'Your cart is empty')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {items.length > 0 && (
                  <button
                    onClick={clearCart}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-red-50"
                    aria-label={t('cart.clear', 'Clear cart')}
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="p-2 text-gray-400 hover:text-ink transition-colors rounded-full hover:bg-gray-100"
                  aria-label={t('cart.close', 'Close cart')}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                  <FaShoppingCart className="w-16 h-16 mb-4 text-gray-200" />
                  <p className="font-medium text-ink mb-1">{t('cart.empty_title', 'No items yet')}</p>
                  <p className="text-sm">{t('cart.empty_desc', 'Browse our plans and add them to your cart.')}</p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((entry) => (
                    <li
                      key={entry.id}
                      className="bg-gray-50 rounded-2xl p-4 border border-gray-100"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <p className="font-bold text-ink text-sm leading-tight">{entry.name}</p>
                          <p className="text-xs text-ash mt-0.5">Plan {entry.planNumber}</p>
                        </div>
                        <button
                          onClick={() => removeItem(entry.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors shrink-0"
                          aria-label={t('cart.remove', 'Remove')}
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-ash uppercase tracking-wider font-semibold">{t('cart.unit_price', 'Unit Price')}</p>
                          <p className="font-bold text-ink">{formatPrice(entry.price)}</p>
                          {entry.qty > 1 && (
                            <p className="text-xs text-ash mt-0.5">
                              {t('cart.line_total', 'Total')}: {formatPrice(entry.price * entry.qty)}
                            </p>
                          )}
                        </div>

                        {/* Quantity controls */}
                        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-2 py-1">
                          <button
                            onClick={() => decrement(entry.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-ink transition-colors"
                            aria-label={t('cart.decrease', 'Decrease')}
                          >
                            <FaMinus className="w-3 h-3" />
                          </button>
                          <span className="w-6 text-center font-bold text-sm text-ink">{entry.qty}</span>
                          <button
                            onClick={() => increment(entry.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
                            aria-label={t('cart.increase', 'Increase')}
                          >
                            <FaPlus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Footer / Checkout */}
            {items.length > 0 && (
              <div className="border-t border-gray-100 px-6 py-4 space-y-4 bg-gray-50">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ash font-medium">{t('cart.total_items', 'Items')}</span>
                  <span className="font-bold text-ink">{totalUnits}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-ink">{t('cart.estimated_total', 'Estimated Total')}</span>
                  <span className="text-2xl font-extrabold text-primary">{formatPrice(subtotal)}</span>
                </div>

                {/* WhatsApp checkout — disabled, Stripe is now primary
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] hover:bg-[#1da851] text-white font-bold rounded-xl shadow-lg hover:shadow-[#25D366]/30 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <FaWhatsapp className="w-5 h-5" />
                  {t('cart.checkout_whatsapp', 'Order via WhatsApp')}
                </a>

                <p className="text-xs text-gray-400 text-center">
                  {t('cart.checkout_note', 'Your order will be sent via WhatsApp for confirmation.')}
                </p>
                */}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
